import React, { useState } from "react";
import { RazorPayPayment } from "../Payment/RazorPayPayment";
import LoaderOverlay from "src/Payment/LoaderOverlay";

interface CartPageProps {
    items: any[];
    isDiscount: boolean;
    AddNewSponsorGrowniverseId: string;
}

const CartPage: React.FC<CartPageProps> = ({
    items,
    isDiscount,
    AddNewSponsorGrowniverseId,
}) => {
    const [loader, setLoader] = useState(false);

    /* =========================================================
       PRICE HELPERS
    ========================================================= */

    const roundHalfDown = (num: number) => {
        const decimal = num % 1;
        const integer = Math.floor(num);

        if (decimal === 0.5) {
            return integer;
        }

        return Math.round(num);
    };

    const discountCourses = (coursePrice: number) => {
        return Math.round(coursePrice - coursePrice * 0.1);
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat("en-IN").format(
            Number(price) || 0
        );
    };

    /* =========================================================
       TOTALS
    ========================================================= */

    const subtotal = items.reduce(
        (acc, item) =>
            acc + (Number(item?.coursePrice) || 0),
        0
    );

    const roundedSubtotal = roundHalfDown(subtotal);

    const finalAmount = isDiscount
        ? discountCourses(subtotal)
        : roundedSubtotal;

    const discountAmount = isDiscount
        ? Math.max(
              0,
              roundedSubtotal - discountCourses(subtotal)
          )
        : 0;

    /* =========================================================
       PAYMENT
    ========================================================= */

    const handleCartPayment = () => {
        if (!items?.length || loader) return;

        setLoader(true);

        const courseIds =
            items?.map((item) => item.courseId) ?? null;

        RazorPayPayment(
            finalAmount * 100,
            0,
            AddNewSponsorGrowniverseId,
            courseIds
        );
    };

    /* =========================================================
       EMPTY CART
    ========================================================= */

    if (!items || items.length === 0) {
        return (
            <>
                {loader && <LoaderOverlay open={loader} />}

                <div className="cart-page">
                    <div className="cart-background">
                        <div className="cart-glow cart-glow-one" />
                        <div className="cart-glow cart-glow-two" />
                        <div className="cart-grid" />
                    </div>

                    <div className="cart-container">
                        <div className="cart-empty-header">
                            <span className="cart-eyebrow">
                                GROWNIVERSE / CART
                            </span>

                            <h1>
                                Your{" "}
                                <span>Learning Cart</span>
                            </h1>

                            <p>
                                Your selected courses will appear
                                here when you add them.
                            </p>
                        </div>

                        <div className="empty-cart-card">
                            <div className="empty-cart-icon">
                                🛒
                            </div>

                            <span className="empty-label">
                                NO COURSES SELECTED
                            </span>

                            <h2>Your cart is empty</h2>

                            <p>
                                Explore our learning library and
                                start building your personalized
                                learning journey.
                            </p>

                            <div className="empty-cart-line">
                                <span />
                                <b>GROWNIVERSE</b>
                                <span />
                            </div>
                        </div>
                    </div>

                    <style>
                        {cartStyles}
                    </style>
                </div>
            </>
        );
    }

    /* =========================================================
       MAIN
    ========================================================= */

    return (
        <>
            {loader && <LoaderOverlay open={loader} />}

            <div className="cart-page">
                {/* =================================================
                    BACKGROUND
                ================================================= */}

                <div className="cart-background">
                    <div className="cart-glow cart-glow-one" />
                    <div className="cart-glow cart-glow-two" />
                    <div className="cart-glow cart-glow-three" />

                    <div className="cart-grid" />

                    <div className="cart-noise" />
                </div>

                {/* =================================================
                    MAIN CONTAINER
                ================================================= */}

                <div className="cart-container">

                    {/* =================================================
                        HEADER
                    ================================================= */}

                    <header className="cart-header">

                        <div className="cart-header-left">

                            <div className="cart-breadcrumb">
                                <span>GROWNIVERSE</span>
                                <b>/</b>
                                <strong>CART</strong>
                            </div>

                            <div className="cart-title-row">

                                <div>
                                    <span className="cart-eyebrow">
                                        SECURE LEARNING CHECKOUT
                                    </span>

                                    <h1>
                                        Complete Your{" "}
                                        <span>Learning</span>
                                    </h1>

                                    <p>
                                        Review your selected
                                        courses and continue to
                                        secure checkout.
                                    </p>
                                </div>

                            </div>

                        </div>

                        {/* CART COUNT */}

                        <div className="cart-count-card">

                            <div className="cart-count-icon">
                                🛒
                            </div>

                            <div>
                                <strong>
                                    {items.length}
                                </strong>

                                <span>
                                    {items.length === 1
                                        ? "Course selected"
                                        : "Courses selected"}
                                </span>
                            </div>

                        </div>

                    </header>

                    {/* =================================================
                        TRUST STRIP
                    ================================================= */}

                    <div className="trust-strip">

                        <div className="trust-item">
                            <div className="trust-icon">
                                ✓
                            </div>

                            <div>
                                <strong>
                                    Secure Checkout
                                </strong>

                                <span>
                                    Protected payment
                                </span>
                            </div>
                        </div>

                        <div className="trust-divider" />

                        <div className="trust-item">
                            <div className="trust-icon">
                                ⚡
                            </div>

                            <div>
                                <strong>
                                    Instant Access
                                </strong>

                                <span>
                                    Start learning quickly
                                </span>
                            </div>
                        </div>

                        <div className="trust-divider" />

                        <div className="trust-item">
                            <div className="trust-icon">
                                ◈
                            </div>

                            <div>
                                <strong>
                                    Premium Content
                                </strong>

                                <span>
                                    Curated learning
                                </span>
                            </div>
                        </div>

                    </div>

                    {/* =================================================
                        CHECKOUT GRID
                    ================================================= */}

                    <div className="checkout-grid">

                        {/* =================================================
                            LEFT
                        ================================================= */}

                        <section className="courses-panel">

                            <div className="panel-header">

                                <div>
                                    <span className="panel-label">
                                        YOUR SELECTION
                                    </span>

                                    <h2>
                                        Selected Courses
                                    </h2>

                                    <p>
                                        Everything you're about
                                        to purchase.
                                    </p>
                                </div>

                                {isDiscount && (
                                    <div className="discount-badge">
                                        <span>✓</span>
                                        10% SAVING
                                    </div>
                                )}

                            </div>

                            {/* COURSE LIST */}

                            <div className="course-list">

                                {items.map(
                                    (item, index) => {

                                        const originalPrice =
                                            roundHalfDown(
                                                Number(
                                                    item.coursePrice
                                                )
                                            );

                                        const currentPrice =
                                            isDiscount
                                                ? discountCourses(
                                                      Number(
                                                          item.coursePrice
                                                      )
                                                  )
                                                : originalPrice;

                                        return (
                                            <article
                                                key={
                                                    item.courseId ??
                                                    index
                                                }
                                                className="cart-course"
                                            >

                                                {/* NUMBER */}

                                                <div className="course-number">
                                                    {String(
                                                        index + 1
                                                    ).padStart(
                                                        2,
                                                        "0"
                                                    )}
                                                </div>

                                                {/* COURSE INFO */}

                                                <div className="course-info">

                                                    <div className="course-meta">
                                                        <span>
                                                            COURSE
                                                        </span>

                                                        <i>
                                                            •
                                                        </i>

                                                        <small>
                                                            DIGITAL
                                                        </small>
                                                    </div>

                                                    <h3>
                                                        {
                                                            item.courseName
                                                        }
                                                    </h3>

                                                    <div className="course-benefits">

                                                        <span>
                                                            ✓ Lifetime Access
                                                        </span>

                                                        <span>
                                                            ✓ Digital Learning
                                                        </span>

                                                    </div>

                                                </div>

                                                {/* PRICE */}

                                                <div className="course-price">

                                                    {isDiscount && (
                                                        <span className="course-old-price">
                                                            ₹
                                                            {formatPrice(
                                                                originalPrice
                                                            )}
                                                        </span>
                                                    )}

                                                    <strong>
                                                        ₹
                                                        {formatPrice(
                                                            currentPrice
                                                        )}
                                                    </strong>

                                                    {isDiscount && (
                                                        <small>
                                                            10% OFF
                                                        </small>
                                                    )}

                                                </div>

                                            </article>
                                        );
                                    }
                                )}

                            </div>

                            {/* =================================================
                                BENEFITS
                            ================================================= */}

                            <div className="benefits-section">

                                <div className="benefit-card">

                                    <div className="benefit-icon">
                                        🔐
                                    </div>

                                    <div>
                                        <strong>
                                            Secure Payment
                                        </strong>

                                        <span>
                                            Your transaction is
                                            securely processed.
                                        </span>
                                    </div>

                                </div>

                                <div className="benefit-card">

                                    <div className="benefit-icon">
                                        ⚡
                                    </div>

                                    <div>
                                        <strong>
                                            Instant Learning
                                        </strong>

                                        <span>
                                            Access your courses
                                            after payment.
                                        </span>
                                    </div>

                                </div>

                                <div className="benefit-card">

                                    <div className="benefit-icon">
                                        ◉
                                    </div>

                                    <div>
                                        <strong>
                                            Premium Experience
                                        </strong>

                                        <span>
                                            Carefully curated
                                            learning content.
                                        </span>
                                    </div>

                                </div>

                            </div>

                        </section>

                        {/* =================================================
                            RIGHT SUMMARY
                        ================================================= */}

                        <aside className="summary-wrapper">

                            <div className="summary-card">

                                {/* SUMMARY HEADER */}

                                <div className="summary-header">

                                    <div className="summary-header-content">

                                        <span>
                                            CHECKOUT
                                        </span>

                                        <h2>
                                            Order Summary
                                        </h2>

                                        <p>
                                            Your final purchase
                                            details.
                                        </p>

                                    </div>

                                    <div className="summary-check">
                                        ✓
                                    </div>

                                </div>

                                {/* SUMMARY BODY */}

                                <div className="summary-body">

                                    {/* ITEMS */}

                                    <div className="summary-row">
                                        <span>
                                            Courses
                                        </span>

                                        <strong>
                                            {items.length}
                                        </strong>
                                    </div>

                                    {/* SUBTOTAL */}

                                    <div className="summary-row">
                                        <span>
                                            Course Subtotal
                                        </span>

                                        <strong>
                                            ₹
                                            {formatPrice(
                                                roundedSubtotal
                                            )}
                                        </strong>
                                    </div>

                                    {/* DISCOUNT */}

                                    {isDiscount && (
                                        <div className="summary-row discount-row">

                                            <span>
                                                Sponsor Discount
                                            </span>

                                            <strong>
                                                − ₹
                                                {formatPrice(
                                                    discountAmount
                                                )}
                                            </strong>

                                        </div>
                                    )}

                                    <div className="summary-divider" />

                                    {/* TOTAL */}

                                    <div className="total-box">

                                        <div>
                                            <span>
                                                TOTAL PAYABLE
                                            </span>

                                            <small>
                                                Inclusive final
                                                amount
                                            </small>
                                        </div>

                                        <strong>
                                            ₹
                                            {formatPrice(
                                                finalAmount
                                            )}
                                        </strong>

                                    </div>

                                    {/* DISCOUNT MESSAGE */}

                                    {isDiscount && (
                                        <div className="saved-message">
                                            <span>✓</span>

                                            <div>
                                                <strong>
                                                    You're saving ₹
                                                    {formatPrice(
                                                        discountAmount
                                                    )}
                                                </strong>

                                                <small>
                                                    Sponsor pricing
                                                    has been applied.
                                                </small>
                                            </div>
                                        </div>
                                    )}

                                    {/* PAYMENT BUTTON */}

                                    <button
                                        className="secure-payment-button"
                                        onClick={
                                            handleCartPayment
                                        }
                                        disabled={loader}
                                    >

                                        <div>
                                            <span>
                                                PROCEED TO PAYMENT
                                            </span>

                                            <small>
                                                Secure checkout
                                            </small>
                                        </div>

                                        <b>
                                            →
                                        </b>

                                    </button>

                                    {/* RAZORPAY */}

                                    <div className="payment-provider">

                                        <span>
                                            🔒
                                        </span>

                                        Securely processed by{" "}

                                        <strong>
                                            Razorpay
                                        </strong>

                                    </div>

                                </div>

                            </div>

                            {/* SECURITY */}

                            <div className="security-note">

                                <div className="security-icon">
                                    🛡
                                </div>

                                <div>
                                    <strong>
                                        Your payment is protected
                                    </strong>

                                    <span>
                                        Your payment information is
                                        encrypted and handled securely.
                                    </span>
                                </div>

                            </div>

                        </aside>

                    </div>

                    {/* =================================================
                        FOOTER NOTE
                    ================================================= */}

                    <div className="checkout-footer">

                        <span>
                            GROWNIVERSE
                        </span>

                        <div />

                        <small>
                            Premium digital learning experience
                        </small>

                    </div>

                </div>

                <style>
                    {cartStyles}
                </style>

            </div>
        </>
    );
};

const cartStyles = `

/* =========================================================
   PAGE
========================================================= */

.cart-page {
    min-height: 100vh;
    width: 100%;
    position: relative;
    overflow: hidden;
    background:
        radial-gradient(
            circle at 20% 10%,
            rgba(30,136,229,.12),
            transparent 32%
        ),
        radial-gradient(
            circle at 90% 80%,
            rgba(37,218,115,.07),
            transparent 28%
        ),
        linear-gradient(
            135deg,
            #02050a 0%,
            #06111d 48%,
            #02060b 100%
        );
    font-family:
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        Inter,
        sans-serif;
    color: #fff;
    padding: 42px 30px 70px;
    box-sizing: border-box;
}

/* =========================================================
   BACKGROUND
========================================================= */

.cart-background {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
}

.cart-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
}

.cart-glow-one {
    width: 500px;
    height: 500px;
    top: -300px;
    left: -220px;
    background: rgba(30,136,229,.13);
}

.cart-glow-two {
    width: 450px;
    height: 450px;
    bottom: -280px;
    right: -180px;
    background: rgba(37,218,115,.08);
}

.cart-glow-three {
    width: 350px;
    height: 350px;
    top: 38%;
    right: 25%;
    background: rgba(90,80,255,.045);
}

.cart-grid {
    position: absolute;
    inset: 0;
    opacity: .13;
    background-image:
        linear-gradient(
            rgba(255,255,255,.035) 1px,
            transparent 1px
        ),
        linear-gradient(
            90deg,
            rgba(255,255,255,.035) 1px,
            transparent 1px
        );
    background-size: 55px 55px;
}

.cart-noise {
    position: absolute;
    inset: 0;
    opacity: .025;
    background-image:
        radial-gradient(
            rgba(255,255,255,.8) 1px,
            transparent 1px
        );
    background-size: 4px 4px;
}

/* =========================================================
   CONTAINER
========================================================= */

.cart-container {
    position: relative;
    z-index: 5;
    width: 100%;
    max-width: 1320px;
    margin: 0 auto;
    box-sizing: border-box;
}

/* =========================================================
   HEADER
========================================================= */

.cart-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 35px;
    margin-bottom: 30px;
}

.cart-breadcrumb {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 14px;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 1.5px;
    color: #607084;
}

.cart-breadcrumb b {
    color: #283849;
}

.cart-breadcrumb strong {
    color: #1e88e5;
}

.cart-eyebrow {
    display: inline-block;
    color: #668096;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 2px;
    margin-bottom: 8px;
}

.cart-header h1,
.cart-empty-header h1 {
    margin: 0;
    font-size: clamp(32px, 4vw, 48px);
    line-height: 1.08;
    letter-spacing: -2px;
    font-weight: 850;
    color: #fff;
}

.cart-header h1 span,
.cart-empty-header h1 span {
    background:
        linear-gradient(
            90deg,
            #1e88e5,
            #25da73
        );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.cart-header p,
.cart-empty-header p {
    margin: 12px 0 0;
    color: #77889b;
    font-size: 13px;
    line-height: 1.6;
}

.cart-count-card {
    min-width: 175px;
    padding: 13px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    border-radius: 16px;
    background: rgba(255,255,255,.045);
    border: 1px solid rgba(255,255,255,.08);
    backdrop-filter: blur(18px);
    box-sizing: border-box;
}

.cart-count-icon {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background:
        linear-gradient(
            135deg,
            rgba(30,136,229,.18),
            rgba(30,136,229,.04)
        );
    border: 1px solid rgba(30,136,229,.2);
    font-size: 18px;
}

.cart-count-card strong {
    display: block;
    color: #fff;
    font-size: 18px;
}

.cart-count-card span {
    display: block;
    color: #6e7f91;
    font-size: 9px;
    margin-top: 2px;
}

/* =========================================================
   TRUST STRIP
========================================================= */

.trust-strip {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 14px 18px;
    margin-bottom: 26px;
    box-sizing: border-box;
    border-radius: 15px;
    background: rgba(255,255,255,.035);
    border: 1px solid rgba(255,255,255,.065);
    backdrop-filter: blur(16px);
}

.trust-item {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
}

.trust-icon {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    color: #25da73;
    background: rgba(37,218,115,.08);
    border: 1px solid rgba(37,218,115,.14);
    font-size: 12px;
}

.trust-item strong {
    display: block;
    color: #dce5ee;
    font-size: 10px;
}

.trust-item span {
    display: block;
    margin-top: 3px;
    color: #657487;
    font-size: 8px;
}

.trust-divider {
    width: 1px;
    height: 28px;
    background: rgba(255,255,255,.07);
}

/* =========================================================
   CHECKOUT GRID
========================================================= */

.checkout-grid {
    display: grid;
    grid-template-columns:
        minmax(0, 1fr)
        390px;
    gap: 28px;
    align-items: start;
}

/* =========================================================
   COURSES PANEL
========================================================= */

.courses-panel {
    min-width: 0;
    overflow: hidden;
    border-radius: 22px;
    background: #fff;
    box-shadow:
        0 30px 90px rgba(0,0,0,.28);
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    padding: 26px 28px;
    border-bottom: 1px solid #edf1f5;
}

.panel-label {
    display: block;
    margin-bottom: 6px;
    color: #1e88e5;
    font-size: 8px;
    font-weight: 850;
    letter-spacing: 1.6px;
}

.panel-header h2 {
    margin: 0;
    color: #141b24;
    font-size: 20px;
    font-weight: 800;
}

.panel-header p {
    margin: 5px 0 0;
    color: #929daa;
    font-size: 11px;
}

.discount-badge {
    display: flex;
    align-items: center;
    gap: 7px;
    flex-shrink: 0;
    padding: 8px 12px;
    border-radius: 9px;
    background: #eaf9ef;
    color: #168044;
    font-size: 9px;
    font-weight: 850;
}

.discount-badge span {
    font-size: 12px;
}

/* =========================================================
   COURSE LIST
========================================================= */

.course-list {
    padding: 0 28px;
}

.cart-course {
    display: grid;
    grid-template-columns: 48px minmax(0,1fr) auto;
    align-items: center;
    gap: 16px;
    padding: 23px 0;
    border-bottom: 1px solid #edf1f5;
    transition: .2s ease;
}

.cart-course:last-child {
    border-bottom: none;
}

.cart-course:hover {
    transform: translateX(3px);
}

.course-number {
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 13px;
    color: #1e88e5;
    background:
        linear-gradient(
            135deg,
            #edf6ff,
            #f7fbff
        );
    border: 1px solid #dfeefa;
    font-size: 10px;
    font-weight: 850;
}

.course-info {
    min-width: 0;
}

.course-meta {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 6px;
}

.course-meta span {
    color: #1e88e5;
    font-size: 7px;
    font-weight: 850;
    letter-spacing: 1.2px;
}

.course-meta i {
    color: #d7dde4;
    font-style: normal;
}

.course-meta small {
    color: #a2aab4;
    font-size: 7px;
    font-weight: 700;
    letter-spacing: .8px;
}

.course-info h3 {
    margin: 0;
    color: #18212c;
    font-size: 14px;
    font-weight: 750;
    line-height: 1.45;
    word-break: break-word;
}

.course-benefits {
    display: flex;
    align-items: center;
    gap: 13px;
    flex-wrap: wrap;
    margin-top: 8px;
}

.course-benefits span {
    color: #71808e;
    font-size: 8px;
}

.course-benefits span:first-child {
    color: #249454;
}

.course-price {
    min-width: 105px;
    text-align: right;
}

.course-price strong {
    display: block;
    color: #17202b;
    font-size: 17px;
    font-weight: 850;
}

.course-old-price {
    display: block;
    margin-bottom: 3px;
    color: #a3abb5;
    font-size: 9px;
    text-decoration: line-through;
}

.course-price small {
    display: inline-block;
    margin-top: 4px;
    color: #199152;
    font-size: 7px;
    font-weight: 850;
    letter-spacing: .8px;
}

/* =========================================================
   BENEFITS
========================================================= */

.benefits-section {
    display: grid;
    grid-template-columns:
        repeat(3, 1fr);
    gap: 12px;
    padding: 19px 28px;
    background:
        linear-gradient(
            135deg,
            #f8fbff,
            #f7fafc
        );
    border-top: 1px solid #edf1f5;
}

.benefit-card {
    display: flex;
    align-items: center;
    gap: 9px;
}

.benefit-icon {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    background: #fff;
    border: 1px solid #e6ebf0;
    font-size: 13px;
}

.benefit-card strong {
    display: block;
    color: #26303b;
    font-size: 9px;
    font-weight: 800;
}

.benefit-card span {
    display: block;
    margin-top: 3px;
    color: #929ba6;
    font-size: 7px;
    line-height: 1.4;
}

/* =========================================================
   SUMMARY
========================================================= */

.summary-wrapper {
    position: sticky;
    top: 25px;
}

.summary-card {
    overflow: hidden;
    border-radius: 22px;
    background: #fff;
    box-shadow:
        0 30px 90px rgba(0,0,0,.3);
}

.summary-header {
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 25px 25px;
    background:
        radial-gradient(
            circle at 100% 0,
            rgba(30,136,229,.18),
            transparent 40%
        ),
        linear-gradient(
            135deg,
            #101c2b,
            #07111c
        );
}

.summary-header-content span {
    display: block;
    margin-bottom: 6px;
    color: #6f8296;
    font-size: 8px;
    font-weight: 850;
    letter-spacing: 1.8px;
}

.summary-header h2 {
    margin: 0;
    color: #fff;
    font-size: 20px;
    font-weight: 800;
}

.summary-header p {
    margin: 5px 0 0;
    color: #64778b;
    font-size: 9px;
}

.summary-check {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #25da73;
    background: rgba(37,218,115,.1);
    border: 1px solid rgba(37,218,115,.2);
    font-size: 12px;
}

.summary-body {
    padding: 25px;
}

.summary-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 15px;
}

.summary-row span {
    color: #727d89;
    font-size: 11px;
}

.summary-row strong {
    color: #252e38;
    font-size: 13px;
    font-weight: 750;
}

.discount-row span,
.discount-row strong {
    color: #209454;
}

.summary-divider {
    height: 1px;
    margin: 20px 0;
    background: #e8edf2;
}

.total-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    padding: 16px;
    margin-bottom: 16px;
    border-radius: 14px;
    background:
        linear-gradient(
            135deg,
            #f6faff,
            #f8fbfd
        );
    border: 1px solid #e4edf6;
}

.total-box span {
    display: block;
    color: #25303c;
    font-size: 10px;
    font-weight: 850;
    letter-spacing: .3px;
}

.total-box small {
    display: block;
    margin-top: 4px;
    color: #9aa4af;
    font-size: 8px;
}

.total-box strong {
    color: #1e88e5;
    font-size: 27px;
    font-weight: 900;
    white-space: nowrap;
}

/* =========================================================
   SAVED MESSAGE
========================================================= */

.saved-message {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 11px 12px;
    margin-bottom: 14px;
    border-radius: 11px;
    background: #effbf3;
    border: 1px solid #d9f2e2;
}

.saved-message > span {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: 7px;
    color: #1a9650;
    background: #dff7e7;
    font-size: 10px;
}

.saved-message strong {
    display: block;
    color: #247d4a;
    font-size: 9px;
}

.saved-message small {
    display: block;
    margin-top: 2px;
    color: #74a388;
    font-size: 7px;
}

/* =========================================================
   PAYMENT BUTTON
========================================================= */

.secure-payment-button {
    width: 100%;
    min-height: 58px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    padding: 8px 10px 8px 18px;
    border: none;
    border-radius: 13px;
    background:
        linear-gradient(
            135deg,
            #1e88e5,
            #1565c0
        );
    color: #fff;
    cursor: pointer;
    box-shadow:
        0 12px 30px rgba(30,136,229,.25);
    transition: .25s ease;
}

.secure-payment-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow:
        0 17px 38px rgba(30,136,229,.38);
}

.secure-payment-button:active:not(:disabled) {
    transform: translateY(0);
}

.secure-payment-button:disabled {
    opacity: .55;
    cursor: not-allowed;
}

.secure-payment-button div {
    text-align: left;
}

.secure-payment-button span {
    display: block;
    font-size: 10px;
    font-weight: 850;
    letter-spacing: .3px;
}

.secure-payment-button small {
    display: block;
    margin-top: 3px;
    color: rgba(255,255,255,.65);
    font-size: 7px;
}

.secure-payment-button b {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: 9px;
    background: rgba(255,255,255,.15);
    font-size: 17px;
}

/* =========================================================
   PROVIDER
========================================================= */

.payment-provider {
    text-align: center;
    margin-top: 13px;
    color: #9aa4ae;
    font-size: 8px;
}

.payment-provider strong {
    color: #64717e;
}

/* =========================================================
   SECURITY
========================================================= */

.security-note {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    padding: 12px 14px;
    border-radius: 13px;
    background: rgba(255,255,255,.035);
    border: 1px solid rgba(255,255,255,.065);
}

.security-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: 9px;
    background: rgba(37,218,115,.07);
    border: 1px solid rgba(37,218,115,.1);
    font-size: 12px;
}

.security-note strong {
    display: block;
    color: #a7b5c3;
    font-size: 8px;
}

.security-note span {
    display: block;
    margin-top: 3px;
    color: #607184;
    font-size: 7px;
    line-height: 1.4;
}

/* =========================================================
   FOOTER
========================================================= */

.checkout-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 28px;
    padding: 0 5px;
}

.checkout-footer span {
    color: #506276;
    font-size: 8px;
    font-weight: 850;
    letter-spacing: 1.5px;
}

.checkout-footer div {
    flex: 1;
    height: 1px;
    background: rgba(255,255,255,.06);
}

.checkout-footer small {
    color: #435467;
    font-size: 8px;
}

/* =========================================================
   EMPTY CART
========================================================= */

.cart-empty-header {
    margin-bottom: 40px;
}

.empty-cart-card {
    max-width: 650px;
    margin: 70px auto 0;
    padding: 65px 40px;
    text-align: center;
    box-sizing: border-box;
    border-radius: 25px;
    background: rgba(255,255,255,.97);
    box-shadow:
        0 35px 100px rgba(0,0,0,.35);
}

.empty-cart-icon {
    width: 82px;
    height: 82px;
    margin: 0 auto 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 23px;
    background:
        linear-gradient(
            135deg,
            #eef6ff,
            #f7fbff
        );
    border: 1px solid #e1effb;
    font-size: 32px;
}

.empty-label {
    color: #1e88e5;
    font-size: 8px;
    font-weight: 850;
    letter-spacing: 1.6px;
}

.empty-cart-card h2 {
    margin: 10px 0 0;
    color: #18202a;
    font-size: 25px;
    font-weight: 850;
}

.empty-cart-card p {
    max-width: 430px;
    margin: 10px auto 25px;
    color: #8c97a3;
    font-size: 12px;
    line-height: 1.6;
}

.empty-cart-line {
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: 220px;
    margin: 25px auto 0;
}

.empty-cart-line span {
    flex: 1;
    height: 1px;
    background: #e4eaf0;
}

.empty-cart-line b {
    color: #a0aab5;
    font-size: 7px;
    letter-spacing: 1.2px;
}

/* =========================================================
   TABLET
========================================================= */

@media (max-width: 1100px) {

    .cart-page {
        padding-left: 24px;
        padding-right: 24px;
    }

    .checkout-grid {
        grid-template-columns:
            minmax(0, 1fr)
            350px;
        gap: 20px;
    }

    .benefits-section {
        grid-template-columns: 1fr 1fr;
    }

    .benefit-card:last-child {
        grid-column: 1 / -1;
    }

}

/* =========================================================
   IPAD / SMALL LAPTOP
========================================================= */

@media (max-width: 900px) {

    .cart-page {
        padding: 35px 22px 60px;
    }

    .cart-header {
        align-items: flex-start;
    }

    .checkout-grid {
        grid-template-columns: 1fr;
    }

    .summary-wrapper {
        position: relative;
        top: auto;
    }

    .summary-card {
        max-width: 100%;
    }

}

/* =========================================================
   MOBILE
========================================================= */

@media (max-width: 650px) {

    .cart-page {
        padding:
            25px
            15px
            45px;
    }

    .cart-header {
        flex-direction: column;
        align-items: stretch;
        gap: 20px;
    }

    .cart-header h1 {
        font-size: 34px;
        letter-spacing: -1.3px;
    }

    .cart-count-card {
        width: 100%;
    }

    .trust-strip {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        padding: 13px;
    }

    .trust-divider {
        display: none;
    }

    .cart-course {
        grid-template-columns:
            42px
            minmax(0,1fr);
        gap: 12px;
    }

    .course-number {
        width: 40px;
        height: 40px;
    }

    .course-price {
        grid-column: 2;
        text-align: left;
        min-width: auto;
        margin-top: -3px;
    }

    .course-price strong {
        display: inline-block;
        margin-right: 7px;
    }

    .course-old-price {
        display: inline-block;
        margin-right: 6px;
    }

    .panel-header {
        padding: 22px 20px;
        align-items: flex-start;
    }

    .course-list {
        padding: 0 20px;
    }

    .benefits-section {
        grid-template-columns: 1fr;
        padding: 18px 20px;
    }

    .benefit-card:last-child {
        grid-column: auto;
    }

    .summary-body {
        padding: 22px 20px;
    }

    .summary-header {
        padding: 22px 20px;
    }

    .total-box strong {
        font-size: 23px;
    }

}

/* =========================================================
   SMALL MOBILE
========================================================= */

@media (max-width: 430px) {

    .cart-page {
        padding:
            20px
            12px
            40px;
    }

    .cart-header h1 {
        font-size: 30px;
    }

    .cart-header p {
        font-size: 11px;
    }

    .trust-strip {
        grid-template-columns: 1fr;
    }

    .trust-item {
        width: 100%;
    }

    .panel-header {
        flex-direction: column;
    }

    .discount-badge {
        align-self: flex-start;
    }

    .course-info h3 {
        font-size: 13px;
    }

    .course-benefits {
        gap: 6px;
        flex-direction: column;
        align-items: flex-start;
    }

    .total-box {
        align-items: flex-start;
    }

    .total-box strong {
        font-size: 21px;
    }

    .empty-cart-card {
        padding: 50px 22px;
    }

}

/* =========================================================
   ACCESSIBILITY
========================================================= */

@media (prefers-reduced-motion: reduce) {

    .cart-course,
    .secure-payment-button {
        transition: none;
    }

}

`;

export default CartPage;